import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { getOrgAccountsScopeValue } from '~/shared/utils/organizationUtils';
import {
  configurationQueryKey,
  configurationTreeQueryKey,
  revertConfiguration,
  type ConfigurationLevel,
} from '~/settings/components/ConfigurationsPage/helpers/configurationApi';

interface RevertParams {
  level: ConfigurationLevel;
  keys: string | string[];
  accountId: string;
  clusterId?: string;
  instanceId?: string;
  region?: string;
}

interface UseRevertConfigurationMutationParams {
  nodeId: string | null;
}

export function useRevertConfigurationMutation({ nodeId }: UseRevertConfigurationMutationParams) {
  const api = useXFetch();
  const queryClient = useQueryClient();
  const { accounts, activeTenantId } = useAuthContext();
  const accountsScope = getOrgAccountsScopeValue(accounts, activeTenantId);

  return useMutation({
    mutationFn: async (params: RevertParams) => {
      const result = await revertConfiguration(api, params);

      if (nodeId) {
        const queryKey = configurationQueryKey({
          nodeId,
          level: params.level,
          clusterId: params.clusterId,
          instanceId: params.instanceId,
          region: params.region,
          accountId: params.accountId,
        });
        queryClient.invalidateQueries({
          queryKey,
          exact: true,
        });

        queryClient.invalidateQueries({
          queryKey: configurationTreeQueryKey(accounts ?? [], 'edited', accountsScope),
          exact: true,
        });

        queryClient.invalidateQueries({
          queryKey: ['configuration'],
          refetchType: 'none',
        });
      }

      return result;
    },
    onError: (error) => {
      console.error('Failed to revert configuration:', error);
    },
  });
}
